import React, { forwardRef, useState, useMemo, useCallback } from 'react'
import {
    Box,
    Typography,
    IconButton,
    MobileStepper,
    Paper,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft } from '@fortawesome/pro-duotone-svg-icons/faChevronLeft'
import { faChevronRight } from '@fortawesome/pro-duotone-svg-icons/faChevronRight'
import { CardBlockProps } from '../types'
import { getImageUrl } from '../../../utils/helpers'

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface CardCarouselProps extends CardBlockProps {}

export const CardCarousel = forwardRef<HTMLDivElement, CardCarouselProps>(
    ({ kedo, block, data, listItems, getValue }, ref) => {
        const [activeStep, setActiveStep] = useState(0)
        const templateOptions = block.settings?.templateOptions

        const fieldConfig = useMemo(() => {
            if (!templateOptions) return null

            return {
                picture: listItems.find(
                    (item) => item.id === templateOptions.picture
                ),
                title: listItems.find(
                    (item) => item.id === templateOptions.title
                ),
                description: listItems.find(
                    (item) => item.id === templateOptions.description
                ),
            }
        }, [templateOptions, listItems])

        const maxSteps = data.dossiers.length

        const handleNext = useCallback(() => {
            setActiveStep((prev) => Math.min(prev + 1, maxSteps - 1))
        }, [maxSteps])

        const handleBack = useCallback(() => {
            setActiveStep((prev) => Math.max(prev - 1, 0))
        }, [])

        if (!templateOptions || !fieldConfig || maxSteps === 0) {
            return null
        }

        const currentDossier = data.dossiers[activeStep]
        const dossierData = data.contents.filter(
            (item) => item.id === currentDossier.id
        )
        const imageUrl = fieldConfig.picture
            ? getImageUrl(fieldConfig.picture, dossierData, kedo)
            : null

        return (
            <Box ref={ref} sx={{ maxWidth: '100%', flexGrow: 1 }}>
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        height: 400,
                        overflow: 'hidden',
                    }}
                >
                    {imageUrl && (
                        <Box
                            component="img"
                            src={imageUrl}
                            alt={currentDossier.summary}
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                            }}
                        />
                    )}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bgcolor: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            p: 2,
                        }}
                    >
                        <Typography variant="h5" component="h3">
                            {fieldConfig.title
                                ? getValue(
                                      fieldConfig.title,
                                      dossierData,
                                      currentDossier
                                  )
                                : currentDossier.summary}
                        </Typography>
                        {fieldConfig.description && (
                            <Typography variant="body2">
                                {getValue(
                                    fieldConfig.description,
                                    dossierData,
                                    currentDossier
                                )}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <MobileStepper
                    steps={maxSteps}
                    position="static"
                    activeStep={activeStep}
                    nextButton={
                        <IconButton
                            size="small"
                            onClick={handleNext}
                            disabled={activeStep === maxSteps - 1}
                        >
                            <FontAwesomeIcon icon={faChevronRight} />
                        </IconButton>
                    }
                    backButton={
                        <IconButton
                            size="small"
                            onClick={handleBack}
                            disabled={activeStep === 0}
                        >
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </IconButton>
                    }
                />
            </Box>
        )
    }
)

CardCarousel.displayName = 'CardCarousel'
